import { type NoteInfo, NoteType } from '@compass/client/types/note';
import { useNotes } from '@hooks/useNotes';
import { useUsersByIds } from '@hooks/useUsers';
import { useStore } from '@store/index';
import { Plus } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { AddNoteDialog } from '@/components/AddNoteDialog';
import { FilterChip } from '@/components/FilterChip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { UserAvatar } from '@/components/UserAvatar';
import type { SupportedTimeZone } from '@/constants/date';
import {
  getActionLabelFromWorkflow,
  getStateTransition,
  parseActionFromString,
} from '@/utils/caseActions';
import { formatStatus, getStatusColors } from '@/utils/cases';
import { formatNoteDate } from '@/utils/notes';

interface NotesPanelProps {
  caseRecordId: string;
  canAddNote: boolean;
  headerAction?: React.ReactNode;
  /** Whether to show filter pills (All, Status, Assignee, Case). Defaults to true. */
  showFilters?: boolean;
  /** Custom title for the notes section. Defaults to "Notes". */
  title?: string;
}

interface NoteCardProps {
  note: NoteInfo;
  userName: string;
  timeZone: SupportedTimeZone;
  workflow?: any;
}

const NoteCard: React.FC<NoteCardProps> = ({ note, userName, timeZone, workflow }) => {
  const renderNoteHeader = () => {
    switch (note.type) {
      case NoteType.NOTE_TYPE_STATE_CHANGED: {
        const actionString = note.state_changed?.action;
        const action = parseActionFromString(actionString);
        const transition = action ? getStateTransition(action, workflow?.workflow_type) : null;

        // If transition is found, show from/to status badges
        if (transition) {
          const { from, to } = transition;
          const fromColors = getStatusColors(from, workflow);
          const toColors = getStatusColors(to, workflow);
          const fromLabel = fromColors?.label || formatStatus(from, workflow);
          const toLabel = toColors?.label || formatStatus(to, workflow);
          return (
            <p className="text-sm leading-relaxed">
              <span className="font-medium text-foreground">{userName}</span>{' '}
              <span className="text-muted-foreground">changed the status from</span>{' '}
              <Badge
                variant="outline"
                className={`text-xs inline-flex ${fromColors?.bg} ${fromColors?.text} ${fromColors?.border}`}
              >
                {fromLabel}
              </Badge>{' '}
              <span className="text-muted-foreground">to</span>{' '}
              <Badge
                variant="outline"
                className={`text-xs inline-flex ${toColors?.bg} ${toColors?.text} ${toColors?.border}`}
              >
                {toLabel}
              </Badge>{' '}
              <span className="text-muted-foreground">with a note</span>
            </p>
          );
        }

        // Fallback: show action label if transition not found
        const actionLabel = action ? getActionLabelFromWorkflow(action, workflow) : null;
        if (actionLabel) {
          return (
            <p className="text-sm leading-relaxed">
              <span className="font-medium text-foreground">{userName}</span>{' '}
              <span className="text-muted-foreground">executed</span>{' '}
              <Badge variant="outline" className="text-xs inline-flex">
                {actionLabel}
              </Badge>{' '}
              <span className="text-muted-foreground">action with a note</span>
            </p>
          );
        }

        // Final fallback
        return (
          <p className="text-sm">
            <span className="font-medium text-foreground">{userName}</span>{' '}
            <span className="text-muted-foreground">changed the status with a note</span>
          </p>
        );
      }
      case NoteType.NOTE_TYPE_ASSIGNMENT_CHANGED: {
        const fromUser = note.assignment_changed?.from_user || 'Unassigned';
        const toUser = note.assignment_changed?.to_user || '';
        return (
          <p className="text-sm">
            <span className="font-medium text-foreground">{userName}</span>{' '}
            <span className="text-muted-foreground">changed assignee from</span>{' '}
            <span className="text-foreground">{fromUser}</span>{' '}
            <span className="text-muted-foreground">to</span>{' '}
            <span className="text-foreground">{toUser}</span>{' '}
            <span className="text-muted-foreground">with a note</span>
          </p>
        );
      }
      case NoteType.NOTE_TYPE_GENERAL:
      default:
        return (
          <p className="text-sm">
            <span className="font-medium text-foreground">{userName}</span>{' '}
            <span className="text-muted-foreground">added a note</span>
          </p>
        );
    }
  };

  return (
    <div className="space-y-2">
      {/* Header row: Avatar + text */}
      <div className="flex items-center gap-2">
        <UserAvatar name={userName} userId={note.created_by} size="md" className="shrink-0" />
        {renderNoteHeader()}
      </div>

      {/* Note content card */}
      <div className="p-3 bg-muted/50 rounded-lg">
        <p className="text-sm text-foreground">{note.content}</p>
      </div>

      {/* Footer */}
      <p className="text-xs text-muted-foreground">{formatNoteDate(note.created_at, timeZone)}</p>
    </div>
  );
};

const NotesLoadingSkeleton: React.FC = () => (
  <div className="space-y-6">
    {[1, 2, 3].map(i => (
      <div key={i} className="space-y-2">
        {/* Header row: Avatar + text */}
        <div className="flex items-center gap-2">
          <Skeleton className="size-8 rounded-full shrink-0" />
          <Skeleton className="h-4 w-48" />
        </div>
        {/* Content card */}
        <Skeleton className="h-16 w-full rounded-lg" />
        {/* Timestamp */}
        <Skeleton className="h-3 w-32" />
      </div>
    ))}
  </div>
);

export function NotesPanel({
  caseRecordId,
  canAddNote,
  headerAction,
  showFilters = true,
  title = 'Notes',
}: NotesPanelProps) {
  const [isAddNoteDialogOpen, setIsAddNoteDialogOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<NoteType>(NoteType.NOTE_TYPE_UNSPECIFIED);
  const { timeZone, getSelectedWorkflow } = useStore();
  const workflow = getSelectedWorkflow();

  // Fetch notes from API
  const { data: notesData, isLoading, error } = useNotes(caseRecordId);
  const notes = useMemo(() => notesData?.notes || [], [notesData?.notes]);

  // Extract unique user IDs from notes for fetching user details
  const userIds = useMemo(() => {
    return notes.map(note => note.created_by).filter((id): id is string => !!id);
  }, [notes]);

  // Fetch user details for all note creators
  const { usersMap, isLoading: isLoadingUsers } = useUsersByIds(userIds);

  // Get user name for a note
  const getUserName = (userId: string | undefined): string => {
    if (!userId) return 'Unknown';
    const user = usersMap.get(userId);
    return user?.name || user?.email || 'Unknown';
  };

  // Filter counts
  const filterCounts = useMemo(
    () => ({
      all: notes.length,
      status: notes.filter(n => n.type === NoteType.NOTE_TYPE_STATE_CHANGED).length,
      assignee: notes.filter(n => n.type === NoteType.NOTE_TYPE_ASSIGNMENT_CHANGED).length,
      case: notes.filter(n => n.type === NoteType.NOTE_TYPE_GENERAL).length,
    }),
    [notes],
  );

  // Filter notes based on active filter
  const filteredNotes = useMemo(
    () =>
      notes.filter(note => {
        // NOTE_TYPE_UNSPECIFIED means "All" - show all notes
        if (activeFilter === NoteType.NOTE_TYPE_UNSPECIFIED) {
          return true;
        }
        return note.type === activeFilter;
      }),
    [notes, activeFilter],
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header with Add Button */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">{title}</h3>
          <div className="flex items-center gap-1">
            {canAddNote && (
              <Button
                variant="outline"
                size="sm"
                className="h-5 px-2 text-xs"
                onClick={() => setIsAddNoteDialogOpen(true)}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Note
              </Button>
            )}
            {headerAction}
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="px-4 pb-3">
          <div className="flex flex-wrap gap-1.5">
            <FilterChip
              label="All"
              count={filterCounts.all}
              active={activeFilter === NoteType.NOTE_TYPE_UNSPECIFIED}
              onClick={() => setActiveFilter(NoteType.NOTE_TYPE_UNSPECIFIED)}
            />
            <FilterChip
              label="Status"
              count={filterCounts.status}
              active={activeFilter === NoteType.NOTE_TYPE_STATE_CHANGED}
              onClick={() => setActiveFilter(NoteType.NOTE_TYPE_STATE_CHANGED)}
            />
            <FilterChip
              label="Assignee"
              count={filterCounts.assignee}
              active={activeFilter === NoteType.NOTE_TYPE_ASSIGNMENT_CHANGED}
              onClick={() => setActiveFilter(NoteType.NOTE_TYPE_ASSIGNMENT_CHANGED)}
            />
            <FilterChip
              label="Case"
              count={filterCounts.case}
              active={activeFilter === NoteType.NOTE_TYPE_GENERAL}
              onClick={() => setActiveFilter(NoteType.NOTE_TYPE_GENERAL)}
            />
          </div>
        </div>
      )}

      {/* Notes List */}
      <div className="flex-1 overflow-y-auto px-4 pb-3">
        {isLoading || isLoadingUsers ? (
          <NotesLoadingSkeleton />
        ) : error ? (
          <div className="text-center py-8 text-destructive">
            <p className="text-sm">Failed to load notes</p>
          </div>
        ) : filteredNotes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">No notes found</p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredNotes.map(note => (
              <NoteCard
                key={note.id}
                note={note}
                userName={getUserName(note.created_by)}
                timeZone={timeZone}
                workflow={workflow}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Note Dialog */}
      <AddNoteDialog
        isOpen={isAddNoteDialogOpen}
        onClose={() => setIsAddNoteDialogOpen(false)}
        caseRecordId={caseRecordId}
      />
    </div>
  );
}
